import React, { useState, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Gathering } from '../../../types';
import { fetchWithTokenRefresh } from '../../../services/api';
import GatheringDetailModal from './gathering-detail/GatheringDetailModal';
import { TextField, Button, CircularProgress, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface SearchResults {
  items: Gathering[];
  total_count: number;
  total_pages: number;
  current_page: number;
}

const PastGatheringsPage: React.FC = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<Gathering[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Modal state
  const [selectedGatheringId, setSelectedGatheringId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!user?.token || !searchQuery.trim()) return;
    
    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    
    try {
      // Search gatherings without date filter to include all past gatherings
      // Use the 'title' parameter for title search (API doesn't have a generic 'search' param)
      const searchParams = new URLSearchParams();
      searchParams.append('page', '1');
      searchParams.append('limit', '50');
      searchParams.append('order_by', 'date');
      searchParams.append('order_direction', 'desc');
      searchParams.append('title', searchQuery.trim());
      
      const url = `${API_BASE_URL}/gatherings/search?${searchParams.toString()}`;
      const data: SearchResults = await fetchWithTokenRefresh(url, user.token);
      
      setResults(data.items);
      setTotalCount(data.total_count);
    } catch (err) {
      console.error('Error searching gatherings:', err);
      setError('Failed to search gatherings. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.token, searchQuery]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleViewDetails = (gatheringId: string) => {
    setSelectedGatheringId(gatheringId);
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setSelectedGatheringId(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="past-gatherings-container">
      <div className="past-gatherings-content">
        <div className="past-gatherings-header">
          <h1>Search Past Gatherings</h1>
          <p className="past-gatherings-subtitle">
            Search for gatherings older than 30 days by title
          </p>
        </div>

        <div className="past-gatherings-search-box">
          <TextField
            fullWidth
            placeholder="Search by gathering title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ 
              maxWidth: '600px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isLoading || !searchQuery.trim()}
            sx={{ ml: 2, height: '56px', minWidth: '120px' }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Search'}
          </Button>
        </div>

        {error && (
          <div className="past-gatherings-error">
            {error}
          </div>
        )}

        {hasSearched && !isLoading && (
          <div className="past-gatherings-results">
            <div className="results-header">
              <h3>
                {totalCount === 0 
                  ? 'No gatherings found' 
                  : `Found ${totalCount} gathering${totalCount === 1 ? '' : 's'}`}
              </h3>
            </div>

            {results.length > 0 && (
              <div className="results-list">
                {results.map((gathering) => (
                  <div 
                    key={gathering.id} 
                    className="result-item"
                    onClick={() => handleViewDetails(gathering.id)}
                  >
                    <div className="result-item-main">
                      <h4 className="result-title">{gathering.title}</h4>
                      <span className={`result-status status-${gathering.status.toLowerCase().replace(/\s+/g, '-')}`}>
                        {gathering.status}
                      </span>
                    </div>
                    <div className="result-item-details">
                      <span className="result-date">{formatDate(gathering.date)}</span>
                      {gathering.owner && (
                        <span className="result-host">
                          Host: {gathering.owner.first_name} {gathering.owner.last_name}
                        </span>
                      )}
                      {gathering.space && (
                        <span className="result-space">{gathering.space.name}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="past-gatherings-back">
          <a href="#/gatherings" className="back-link">
            ← Back to Gatherings Board
          </a>
        </div>
      </div>

      {/* Gathering Detail Modal */}
      {isDetailModalOpen && selectedGatheringId && user?.token && (
        <GatheringDetailModal
          gatheringId={selectedGatheringId}
          isOpen={isDetailModalOpen}
          onClose={handleCloseModal}
          token={user.token}
        />
      )}
    </div>
  );
};

export default PastGatheringsPage;
